import { useEffect, useState } from "react";
import { Plus, Copy, Trash2, FileEdit } from "lucide-react";
import { Button } from "@/components/ui/button";
import { listBudgets, createBudget, deleteBudget, duplicateBudget, getCurrentBalance } from "@/utils/budgetApi";
import BudgetEditor from "@/components/budget/BudgetEditor";
import ActualsView from "@/components/budget/ActualsView";
import BudgetVsActual from "@/components/budget/BudgetVsActual";

function defaultBudget() {
  const year = new Date().getFullYear();
  return {
    name: `Presupuesto ${year}`,
    range_type: "annual",
    start_month: `${year}-01`,
    end_month: `${year}-12`,
    opening_balance: 0,
    lines: [],
  };
}

export default function Budgets() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null); // null = lista, id = editar
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("budget");

  async function refresh() {
    setLoading(true);
    setItems(await listBudgets());
    setLoading(false);
  }
  useEffect(() => { refresh(); }, []);

  async function handleCreate() {
    const balance = await getCurrentBalance();
    const created = await createBudget({ ...defaultBudget(), opening_balance: balance });
    setEditingId(created.id);
  }
  async function handleDelete(id) {
    if (!window.confirm("¿Borrar este presupuesto?")) return;
    await deleteBudget(id);
    refresh();
  }
  async function handleDuplicate(id) {
    await duplicateBudget(id);
    refresh();
  }

  if (editingId) {
    return <BudgetEditor budgetId={editingId} onBack={() => { setEditingId(null); refresh(); }} />;
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setTab("budget")}
          className={`px-3 py-1.5 text-sm rounded-md ${tab === "budget" ? "bg-slate-800 text-white" : "text-slate-600 hover:text-slate-900"}`}
        >
          Presupuesto
        </button>
        <button
          onClick={() => setTab("real")}
          className={`px-3 py-1.5 text-sm rounded-md ${tab === "real" ? "bg-slate-800 text-white" : "text-slate-600 hover:text-slate-900"}`}
        >
          Real (histórico)
        </button>
        <button
          onClick={() => setTab("compare")}
          className={`px-3 py-1.5 text-sm rounded-md ${tab === "compare" ? "bg-slate-800 text-white" : "text-slate-600 hover:text-slate-900"}`}
        >
          Presupuesto vs Real
        </button>
      </div>

      {tab === "real" ? (
        <ActualsView />
      ) : tab === "compare" ? (
        <BudgetVsActual />
      ) : (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold">Presupuestos</h1>
              <p className="text-sm text-gray-500">Previsiones de ingresos y gastos con escenarios.</p>
            </div>
            <Button onClick={handleCreate} className="flex items-center gap-2" data-testid="new-budget">
              <Plus className="w-4 h-4" /> Nuevo presupuesto
            </Button>
          </div>

          {loading ? (
            <p className="text-gray-400">Cargando…</p>
          ) : items.length === 0 ? (
            <p className="text-gray-400">Aún no tienes presupuestos. Crea el primero.</p>
          ) : (
            <div className="space-y-2">
              {items.map((b) => (
                <div key={b.id} className="flex items-center justify-between border rounded-lg p-4 bg-white">
                  <div>
                    <div className="font-medium">{b.name}</div>
                    <div className="text-xs text-gray-500">{b.start_month} → {b.end_month} · {b.line_count} líneas</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(b.id)} title="Editar"><FileEdit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDuplicate(b.id)} title="Duplicar"><Copy className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(b.id)} title="Borrar" className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
